import { Link, useLocation } from "react-router-dom";
import { Settings, ClipboardList } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { getLastFilterUrl } from "@/hooks/useRememberFilters";
import { mainNavItems, configItems, type NavItem } from "@/components/layout/nav-items";
import { cn } from "@/lib/utils";

interface DriverNavMenuProps {
  onNavigate?: () => void;
}

export function DriverNavMenu({ onNavigate }: DriverNavMenuProps) {
  const { pathname } = useLocation();
  const { roles, isAdmin } = useUserRole();
  const isDriver = roles.includes("driver");
  const driverOnly = isDriver && roles.every((r) => r === "driver");

  const visibleConfigItems = configItems.filter((item) => !item.adminOnly || isAdmin);

  const driverItem: NavItem = {
    title: "My Assignments",
    icon: ClipboardList,
    path: "/my-assignments",
  };

  const isDriverRoute = pathname === "/my-assignments" || pathname.startsWith("/my-assignments/");

  const renderItem = (item: NavItem, forceActive?: boolean) => {
    const active = forceActive ?? pathname === item.path;
    return (
      <Link
        key={item.path}
        to={getLastFilterUrl(item.path)}
        onClick={onNavigate}
        className={cn(
          "flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent",
          active && "bg-accent font-medium",
        )}
      >
        <item.icon className="h-4 w-4" />
        <span>{item.title}</span>
      </Link>
    );
  };

  return (
    <nav className="flex-1 overflow-y-auto px-2 py-2">
      <div className="flex flex-col">
        {renderItem(driverItem, isDriverRoute)}
        {!driverOnly && mainNavItems.map((item) => renderItem(item))}
      </div>

      {!driverOnly && !isDriver && visibleConfigItems.length > 0 && (
        <>
          <div className="flex items-center gap-2 px-3 pt-4 pb-1 text-xs uppercase tracking-wide text-muted-foreground">
            <Settings className="h-3.5 w-3.5" />
            Configuration
          </div>
          <div className="flex flex-col">
            {visibleConfigItems.map((item) => renderItem(item))}
          </div>
        </>
      )}
    </nav>
  );
}